import { assertNever } from "@ondo/shared";
import type { WorkspaceRef } from "@ondo/shared";
import { Link } from "react-router";

import { LoadingScreen } from "../lib/Loading.tsx";
import { Tag } from "../lib/Tag.tsx";
import { useAccount } from "../lib/account.ts";
import { OrgTeam } from "./OrgTeam.tsx";
import { ProfileAffiliations } from "./ProfileAffiliations.tsx";
import { ProfileContact } from "./ProfileContact.tsx";
import { ProfileEntities } from "./ProfileEntities.tsx";
import { ProfileIdentity } from "./ProfileIdentity.tsx";
import { FieldList, NotYet, Panel } from "./ProfilePanels.tsx";
import { addableMemberships } from "./profile.model.ts";
import { IDENTITY_CHECKS, PROFILE_COPY, TAB_PANEL_ID } from "./profile.constants.ts";
import type { ProfileTabKey } from "./profile.constants.ts";
import { useProfileController } from "./useProfileController.ts";
import type { ProfileController } from "./useProfileController.ts";
import type { OrgPanels, PersonPanels, ProfileTab, ProfileView } from "./profile.model.ts";
import "./profile.css";

/**
 * The account screen, for whichever principal this workspace is.
 *
 * One screen and not two, because a profile belongs to a principal and
 * `ondo.principal_kind` has two values. A personal workspace opens onto the
 * person signed in; an organisation's opens onto the entity, its people, and —
 * on a third tab — the reader's own account, which is a different record with a
 * different writer.
 *
 * That third tab is not there for symmetry. `deriveWorkspaces` gives a personal
 * workspace only to an account whose declared types produce one, so somebody who
 * declared "gallery" and holds a gallery membership may have none at all, and
 * without it their own record would be unreachable from anywhere in ONDO.
 */
export function Profile({
  workspace,
  onSignOut,
}: {
  workspace: WorkspaceRef;
  onSignOut: () => void;
}) {
  const { me: meState, replaceMe } = useAccount();
  const me = meState.status === "ready" ? meState.me : null;

  const controller = useProfileController({ me, workspace, replaceMe });
  const { view } = controller;

  switch (view.kind) {
    case "loading":
      return <LoadingScreen message={view.message} />;

    case "error":
      return (
        <p className="profile__error" role="alert">
          {view.message}
        </p>
      );

    case "ready":
      return (
        <div className="profile">
          <Tabs
            tabs={view.tabs}
            active={view.active}
            onChoose={controller.chooseTab}
          />
          {/* ONE PANEL, NAMED BY WHICHEVER TAB IS OPEN. `Body` switches on
              `view.active` rather than rendering five panels and hiding four,
              so there is one region to name — the arrangement `WorkRecord`
              already uses. Without the pair, a screen reader announces a
              selected tab controlling nothing. */}
          <div
            id={TAB_PANEL_ID}
            role="tabpanel"
            aria-labelledby={tabId(view.active)}
          >
            <Body view={view} controller={controller} onSignOut={onSignOut} />
          </div>
        </div>
      );

    default:
      return assertNever(view);
  }
}

function tabId(key: ProfileTabKey): string {
  return `profile-tab-${key}`;
}

function Tabs({
  tabs,
  active,
  onChoose,
}: {
  tabs: ProfileTab[];
  active: ProfileTabKey;
  onChoose: (tab: ProfileTabKey) => void;
}) {
  return (
    <div className="profile__tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          id={tabId(tab.key)}
          type="button"
          role="tab"
          aria-selected={tab.key === active}
          aria-controls={TAB_PANEL_ID}
          className={`profile__tab${tab.key === active ? " profile__tab--on" : ""}`}
          onClick={() => onChoose(tab.key)}
        >
          {tab.label}
          {/* A count, not a dot, and absent rather than zero — the rule the
              sidebar's nav badge follows. A badge showing zero has to be read
              before it can be ignored. */}
          {tab.count !== null && <span className="profile__tab-count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** The one place a switch over tabs lives. */
function Body({
  view,
  controller,
  onSignOut,
}: {
  view: Extract<ProfileView, { kind: "ready" }>;
  controller: ProfileController;
  onSignOut: () => void;
}) {
  switch (view.active) {
    case "profile":
    case "account":
      return (
        <PersonTab
          panels={view.person}
          controller={controller}
          onSignOut={onSignOut}
          note={view.active === "account" ? PROFILE_COPY.accountNote : null}
        />
      );

    case "entities":
      return <ProfileEntities entities={view.entities} holdings={view.holdings} />;

    case "organisation":
      return view.organisation ? (
        <OrgTab panels={view.organisation} controller={controller} />
      ) : null;

    case "team":
      return view.organisation ? (
        <OrgTeam
          rows={view.organisation.team}
          bundles={view.organisation.bundles}
          invitations={view.organisation.invitations}
          canSetRoles={view.organisation.canSetRoles}
          controller={controller}
        />
      ) : null;

    default:
      return assertNever(view.active);
  }
}

function PersonTab({
  panels,
  controller,
  onSignOut,
  note,
}: {
  panels: PersonPanels;
  controller: ProfileController;
  onSignOut: () => void;
  /** Shown only on an organisation's "Your account" tab, where it is needed. */
  note: string | null;
}) {
  // Derived here rather than in the view-model because it is one filter over a
  // list the model already built — the alternative is a second field on
  // `PersonPanels` that can disagree with the first.
  const addable = addableMemberships(panels.membership);
  const { busy, addMembership: onAddMembership } = controller;

  return (
    <div className="profile__columns">
      <div className="profile__column">
        {note && <p className="profile__lead">{note}</p>}

        <ProfileIdentity card={panels.identity}>
          <button type="button" className="profile__signout" onClick={onSignOut}>
            {PROFILE_COPY.signOut}
          </button>
        </ProfileIdentity>

        <Panel title={PROFILE_COPY.membershipHeading} note={PROFILE_COPY.membershipNote}>
          {panels.membership.every((choice) => !choice.declared) ? (
            <p className="profile__empty">{PROFILE_COPY.membershipNone}</p>
          ) : (
            <ul className="profile__choices">
              {panels.membership
                .filter((choice) => choice.declared)
                .map((choice) => (
                  <li key={choice.value} className="profile__choice profile__choice--on">
                    {choice.label}
                  </li>
                ))}
            </ul>
          )}

          {/*
            BUTTONS, and only for what is not already declared — the undeclared
            half is no longer rendered as an unlit chip beside the lit ones,
            because a grid where every cell is clickable reads as a toggle and
            this one cannot untoggle. What can be added is a different list from
            what is held, so it is drawn as a different list.
          */}
          {addable.length > 0 && (
            <div className="profile__adds">
              <p className="profile__hint">{PROFILE_COPY.membershipAddNote}</p>
              <ul className="profile__choices">
                {addable.map((choice) => (
                  <li key={choice.value}>
                    <button
                      type="button"
                      className="profile__choice profile__choice--add"
                      disabled={busy}
                      onClick={() => onAddMembership(choice.value)}
                    >
                      {busy ? PROFILE_COPY.membershipBusy : PROFILE_COPY.membershipAdd}{" "}
                      {choice.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="profile__hint">{PROFILE_COPY.membershipChange}</p>
        </Panel>

        <Panel title={PROFILE_COPY.summaryHeading}>
          <FieldList fields={panels.summary} />
        </Panel>
      </div>

      <div className="profile__column">
        <Panel title={PROFILE_COPY.identityHeading} note={PROFILE_COPY.identitySub}>
          <NotYet
            lead={PROFILE_COPY.identityNotYet.lead}
            detail={PROFILE_COPY.identityNotYet.detail}
          />
          <ul className="profile__checks">
            {IDENTITY_CHECKS.map((check) => (
              <li className="profile__check" key={check.id}>
                <span className="profile__check-label">{check.label}</span>
                {/* `unverified` and never `pending`: pending would say ONDO is
                    working on it, and nobody is. The dashed tone means an
                    outline nobody has filled in yet — see tag.css. */}
                <Tag tone="unverified">{PROFILE_COPY.identityNotStarted}</Tag>
              </li>
            ))}
          </ul>
        </Panel>

        <ProfileContact
          fields={panels.contact}
          draft={controller.contactDraft}
          busy={controller.busy}
          saved={controller.saved}
          error={controller.actionError}
          onEdit={controller.editContact}
          onCancel={controller.cancelContact}
          onChange={controller.changeContact}
          onSave={controller.saveContact}
        />

        <Panel title={PROFILE_COPY.addressHeading}>
          <NotYet
            lead={PROFILE_COPY.addressNotYet.lead}
            detail={PROFILE_COPY.addressNotYet.detail}
          />
        </Panel>

        <Panel title={PROFILE_COPY.preferencesHeading}>
          <NotYet
            lead={PROFILE_COPY.preferencesNotYet.lead}
            detail={PROFILE_COPY.preferencesNotYet.detail}
          />
        </Panel>

        <ProfileAffiliations
          rows={panels.affiliations}
          busy={controller.busy}
          error={controller.actionError}
          onAdd={controller.addAffiliation}
          onWithdraw={controller.removeAffiliation}
          onSetAudience={controller.setAffiliationAudience}
        />

        <Panel title={PROFILE_COPY.activityHeading}>
          {panels.activity.length === 0 ? (
            <p className="profile__empty">{PROFILE_COPY.activityEmpty}</p>
          ) : (
            <ul className="profile__rows">
              {panels.activity.map((row) => (
                <li className="profile__row profile__row--event" key={row.id}>
                  <span className="profile__event-glyph" aria-hidden="true">
                    {row.glyph}
                  </span>
                  <div className="profile__row-body">
                    {row.href ? (
                      <Link className="profile__row-title profile__row-link" to={row.href}>
                        {row.title}
                      </Link>
                    ) : (
                      <span className="profile__row-title">{row.title}</span>
                    )}
                    <span className="profile__row-meta">{row.detail}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function OrgTab({
  panels,
  controller,
}: {
  panels: OrgPanels;
  controller: ProfileController;
}) {
  const draft = controller.orgDraft;

  return (
    <div className="profile__single">
      {draft ? (
        <Panel title={PROFILE_COPY.orgHeading}>
          <OrgForm draft={draft} controller={controller} />
        </Panel>
      ) : (
        <ProfileIdentity
          card={panels.identity}
          action={
            panels.canEdit ? (
              <button type="button" className="profile__action" onClick={controller.editOrg}>
                {PROFILE_COPY.edit}
              </button>
            ) : undefined
          }
        >
          {/* Said in words rather than by a missing button. `canEdit` comes from
              the database's own definition of admin, so a reader who expected
              the control is told why it is not there. */}
          {!panels.canEdit && <p className="profile__hint">{PROFILE_COPY.orgNotAdmin}</p>}
          {controller.saved && (
            <p className="profile__saved" role="status">
              {PROFILE_COPY.saved}
            </p>
          )}
        </ProfileIdentity>
      )}

      {/* OND-80. Only for an estate — a gallery has no catalogue raisonné, and
          `panels.estate` is null rather than three zeroes so that "0 works"
          cannot be printed about a firm the figure does not describe. */}
      {panels.estate && (
        <Panel title={PROFILE_COPY.estateHeading}>
          <FieldList fields={panels.estate} />
        </Panel>
      )}
    </div>
  );
}

function OrgForm({
  draft,
  controller,
}: {
  draft: OrgPanels["values"];
  controller: ProfileController;
}) {
  return (
    <form
      className="profile__form"
      onSubmit={(event) => {
        event.preventDefault();
        controller.saveOrg();
      }}
    >
      <label className="profile__label">
        {PROFILE_COPY.nameLabel}
        <input
          className="profile__input"
          value={draft.displayName}
          maxLength={120}
          onChange={(event) => controller.changeOrg({ displayName: event.target.value })}
        />
      </label>

      <label className="profile__label">
        {PROFILE_COPY.orgRegisteredAddress}
        <textarea
          className="profile__input profile__input--area"
          value={draft.registeredAddress}
          maxLength={400}
          rows={3}
          onChange={(event) =>
            controller.changeOrg({ registeredAddress: event.target.value })
          }
        />
      </label>

      <label className="profile__label">
        {PROFILE_COPY.orgRegistrationNumber}
        <input
          className="profile__input"
          value={draft.registrationNumber}
          maxLength={200}
          onChange={(event) =>
            controller.changeOrg({ registrationNumber: event.target.value })
          }
        />
      </label>

      {controller.actionError && (
        <p className="profile__error" role="alert">
          {controller.actionError}
        </p>
      )}

      <div className="profile__form-actions">
        <button type="submit" className="profile__primary" disabled={controller.busy}>
          {controller.busy ? PROFILE_COPY.saving : PROFILE_COPY.save}
        </button>
        <button
          type="button"
          className="profile__action"
          onClick={controller.cancelOrg}
          disabled={controller.busy}
        >
          {PROFILE_COPY.cancel}
        </button>
      </div>
    </form>
  );
}
